/**
 * Multi-step modal for uploading DTA source documents.
 *
 * Handles both SDS specification parsing (deterministic, synchronous) and document extraction
 * (AI-powered, async). The SDS path continues into domain selection and batch DTA creation within
 * the modal.
 *
 * Steps: 1. Type select - choose SDS or Document 2. File upload - select file (SDS parses
 * immediately; Document collects vendor) 3. Domain selection (SDS only) - pick forms 4. Creating
 * (SDS only) - concurrent DTA creation with progress 5. Complete (SDS only) - summary with links to
 * created DTAs.
 */

import Modal from "@cloudscape-design/components/modal"
import { useCallback, useRef, useState } from "react"
import type { ParseSpecResponse } from "@/generated/sdk"
import type {
  DTAUploadModalProps,
  Step,
  UploadType,
  DtaCreationType,
  CreationProgress,
} from "./types"
import {
  useCreateDta,
  useUploadAndParseEdc,
  useUploadAndParseSdtm,
} from "../../hooks/queries/useDtas"
import { useVendorList } from "../../hooks/queries/useVendors"
import BlankDtaStep from "./BlankDtaStep"
import CreationProgressStep from "./CreationProgressStep"
import DomainSelectionStep from "./DomainSelectionStep"
import FileUploadStep from "./FileUploadStep"
import { TRIAL_DESIGN_DOMAINS } from "./types"
import { UPLOAD_TYPES } from "./types"
import TypeSelectStep from "./TypeSelectStep"

const CONCURRENCY_LIMIT = 5

/**
 * Maps the current step (and upload type, for the file-upload step) to the modal title. Titles
 * previously lived in each step's own heading; they now render in the Cloudscape Modal header.
 */
function stepHeader(step: Step, uploadType: UploadType | null): string | undefined {
  switch (step) {
    case "type-select":
      return "Upload"
    case "blank-form":
      return "Create Blank DTA"
    case "file-upload":
      if (uploadType === UPLOAD_TYPES.EDC) return "Upload EDC Specification"
      if (uploadType === UPLOAD_TYPES.SDTM) return "Upload SDTM Mapping Specification"
      return "Upload Document"
    case "domain-selection":
      // DomainSelectionStep renders its own Header (it's also used outside the modal)
      return undefined
    case "complete":
      return "Complete"
    case "creating":
      return "Creating DTAs"
  }
}

export default function DTAUploadModal({
  studyId,
  onClose,
  onComplete,
  onDocumentUpload,
}: DTAUploadModalProps) {
  const [step, setStep] = useState<Step>("type-select")
  const [uploadType, setUploadType] = useState<UploadType | null>(null)

  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [parseResult, setParseResult] = useState<ParseSpecResponse | null>(null)

  const [selectedForms, setSelectedForms] = useState<Set<string>>(new Set())

  // Monotonic id for the in-flight parse request; used to ignore a stale parse
  // result when the user navigates away (Back) before it resolves.
  const parseRequestIdRef = useRef(0)

  const { data: vendors = [], error: vendorLoadErrorRaw } = useVendorList()
  const vendorLoadError = vendorLoadErrorRaw
    ? vendorLoadErrorRaw instanceof Error
      ? vendorLoadErrorRaw.message
      : "Failed to load vendors"
    : null

  const [creationProgress, setCreationProgress] = useState<Map<string, CreationProgress>>(new Map())
  const [docUploading, setDocUploading] = useState(false)

  const uploadAndParseEdc = useUploadAndParseEdc(studyId)
  const uploadAndParseSdtm = useUploadAndParseSdtm(studyId)
  const createDta = useCreateDta(studyId)

  const handleTypeSelect = (type: DtaCreationType) => {
    if (type === UPLOAD_TYPES.BLANK) {
      setStep("blank-form")
      return
    }
    setUploadType(type)
    setStep("file-upload")
  }

  const handleSDSFileSelect = async (file: File) => {
    // Guard against a stale parse: if the user navigates away (Back) while the
    // parse is in flight, a later-resolving request must not force the modal
    // forward to domain-selection. Each parse captures the current generation;
    // Back bumps it, invalidating any in-flight request.
    const requestId = ++parseRequestIdRef.current
    setParsing(true)
    setParseError(null)
    const isSdtm = uploadType === UPLOAD_TYPES.SDTM
    try {
      // Unified flow: presign + upload to S3 + parse (parse-edc or parse-sdtm). Both return the
      // same forms[] shape, so downstream domain selection is identical.
      const result = isSdtm
        ? await uploadAndParseSdtm.mutateAsync({ file })
        : await uploadAndParseEdc.mutateAsync({ file })
      if (requestId !== parseRequestIdRef.current) return // superseded (user went Back)
      setParseResult(result)
      if (result.forms) {
        // Default-select the primary domain datasets. Leave unselected the ones a
        // study usually doesn't transfer - the DAE opts into them: SDTM
        // supplemental (SUPP*) and cross-reference (XREF*) datasets by name, and
        // trial-design domains (study-design metadata) by domain code. All are
        // still shown and selectable.
        const isSecondaryDataset = (form: { name: string; domain?: string }) =>
          /^(SUPP|XREF)/i.test(form.name) ||
          TRIAL_DESIGN_DOMAINS.has((form.domain ?? "").toUpperCase())
        setSelectedForms(
          new Set(result.forms.filter((f) => !isSecondaryDataset(f)).map((f) => f.name)),
        )
      }
      setStep("domain-selection")
    } catch (err) {
      if (requestId !== parseRequestIdRef.current) return
      setParseError(
        err instanceof Error
          ? err.message
          : `Failed to parse ${isSdtm ? "SDTM mapping" : "EDC"} specification`,
      )
    } finally {
      if (requestId === parseRequestIdRef.current) setParsing(false)
    }
  }

  const handleDocSubmit = async (file: File, docVendorId: string, docVendorName: string) => {
    setDocUploading(true)
    document.body.style.cursor = "wait"
    try {
      await onDocumentUpload(file, docVendorId, docVendorName)
      onClose()
    } catch {
      // Parent handles error toast
    } finally {
      setDocUploading(false)
      document.body.style.cursor = ""
    }
  }

  const handleBlankCreate = async (vendorId: string, vendorName: string, domain: string) => {
    setParsing(true)
    setParseError(null)
    try {
      const result = await createDta.mutateAsync({
        vendor_id: vendorId,
        vendor_name: vendorName,
        domains: [domain],
        source_type: "third-party",
      })
      onComplete?.()
      onClose()
      // Navigate to the new DTA
      window.location.href = `/dtas/${result.dta_id}`
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Failed to create DTA")
    } finally {
      setParsing(false)
    }
  }

  const toggleForm = (formName: string) => {
    setSelectedForms((prev) => {
      const next = new Set(prev)
      if (next.has(formName)) {
        next.delete(formName)
      } else {
        next.add(formName)
      }
      return next
    })
  }

  const toggleAll = () => {
    if (!parseResult?.forms) return
    if (selectedForms.size === parseResult.forms.length) {
      setSelectedForms(new Set())
    } else {
      setSelectedForms(new Set(parseResult.forms.map((f) => f.name)))
    }
  }

  const startCreation = useCallback(async () => {
    if (!parseResult?.forms) return
    setStep("creating")

    const formsToCreate = parseResult.forms.filter((f) => selectedForms.has(f.name))
    const progress = new Map<string, CreationProgress>()
    for (const form of formsToCreate) {
      progress.set(form.name, { status: "pending" })
    }
    setCreationProgress(new Map(progress))

    const queue = [...formsToCreate]

    // This flow runs only for parsed spec uploads (EDC/SDTM); map to the DTA source type.
    const sourceType = uploadType === UPLOAD_TYPES.SDTM ? UPLOAD_TYPES.SDTM : UPLOAD_TYPES.EDC

    const processOne = async () => {
      while (true) {
        const form = queue.shift()
        if (!form) break

        progress.set(form.name, { status: "creating" })
        setCreationProgress(new Map(progress))

        try {
          const response = await createDta.mutateAsync({
            vendor_id: "",
            vendor_name: "",
            domains: [form.label],
            source_type: sourceType,
            document_content: form.document_content ?? undefined,
          })
          progress.set(form.name, { status: "created", dta_id: response.dta_id })
        } catch (err) {
          progress.set(form.name, {
            status: "failed",
            error: err instanceof Error ? err.message : "Creation failed",
          })
        }

        setCreationProgress(new Map(progress))
      }
    }

    const workerCount = Math.min(CONCURRENCY_LIMIT, formsToCreate.length)
    await Promise.all(Array.from({ length: workerCount }, () => processOne()))
    setStep("complete")
  }, [parseResult, selectedForms, createDta, uploadType])

  const totalCrossFormRules =
    parseResult?.forms
      ?.filter((f) => selectedForms.has(f.name))
      .reduce((sum, f) => sum + f.cross_form_rule_count, 0) ?? 0

  const totalTranslatingRules =
    parseResult?.forms
      ?.filter((f) => selectedForms.has(f.name))
      .reduce((sum, f) => {
        const edcRules = (f.document_content as Record<string, unknown> | null)?._edc_source_rules
        return sum + (Array.isArray(edcRules) ? edcRules.length : 0)
      }, 0) ?? 0

  const modalSize =
    step === "domain-selection"
      ? "x-large"
      : step === "creating" || step === "complete"
        ? "large"
        : "medium"
  const header = stepHeader(step, uploadType)

  return (
    <Modal
      visible
      onDismiss={(event) => {
        // Only the header X closes; ignore backdrop ("overlay") and Esc
        // ("keyboard") so an accidental click-off/Esc never discards work.
        if (event.detail.reason === "closeButton") onClose()
      }}
      header={header}
      size={modalSize}
    >
      {step === "type-select" && <TypeSelectStep onNext={handleTypeSelect} onClose={onClose} />}

      {step === "blank-form" && (
        <BlankDtaStep
          vendors={vendors}
          vendorLoadError={vendorLoadError}
          onSubmit={(vendorId, vendorName, domain) =>
            void handleBlankCreate(vendorId, vendorName, domain)
          }
          onBack={() => {
            setStep("type-select")
            setParseError(null)
          }}
          creating={parsing}
          createError={parseError}
        />
      )}

      {step === "file-upload" && uploadType && (
        <FileUploadStep
          uploadType={uploadType}
          vendors={vendors}
          vendorLoadError={vendorLoadError}
          onBack={() => {
            // Invalidate any in-flight parse so a late result can't drag the
            // user forward to domain-selection after they've gone Back.
            parseRequestIdRef.current++
            setParsing(false)
            setStep("type-select")
            setParseError(null)
          }}
          onSDSFileSelect={(file) => void handleSDSFileSelect(file)}
          parsing={parsing}
          parseError={parseError}
          onDocSubmit={(file, vendorId, vendorName) =>
            void handleDocSubmit(file, vendorId, vendorName)
          }
          docUploading={docUploading}
        />
      )}

      {step === "domain-selection" && parseResult && (
        <DomainSelectionStep
          parseResult={parseResult}
          selectedForms={selectedForms}
          onToggleForm={toggleForm}
          onToggleAll={toggleAll}
          onSubmit={() => void startCreation()}
          onClose={onClose}
          hideSpecColumns={uploadType === UPLOAD_TYPES.SDTM}
          onBack={() => {
            setStep("file-upload")
            setParseResult(null)
            setSelectedForms(new Set())
            setParseError(null)
          }}
        />
      )}

      {(step === "creating" || step === "complete") && (
        <CreationProgressStep
          forms={parseResult?.forms ?? []}
          selectedForms={selectedForms}
          progress={creationProgress}
          isComplete={step === "complete"}
          crossFormRuleCount={totalCrossFormRules}
          translatingRuleCount={totalTranslatingRules}
          parserLabel={parseResult?.parser_label ?? ""}
          onClose={() => {
            onComplete()
            onClose()
          }}
        />
      )}
    </Modal>
  )
}
